import { useEffect, useRef } from 'react';
import { Animated, StyleSheet, View } from 'react-native';
import { useTheme } from '@/contexts/ThemeContext';

interface Props {
  /** Number of skeleton rows to render (default: 5) */
  rows?: number;
}

/**
 * Lightweight skeleton loader — pulsing placeholder rows.
 * No external dependencies. Uses Animated API for subtle opacity pulse.
 */
export default function SkeletonRow({ rows = 5 }: Props) {
  const { theme } = useTheme();
  const opacity = useRef(new Animated.Value(0.4)).current;

  useEffect(() => {
    const pulse = Animated.loop(
      Animated.sequence([
        Animated.timing(opacity, {
          toValue: 1,
          duration: 800,
          useNativeDriver: true,
        }),
        Animated.timing(opacity, {
          toValue: 0.4,
          duration: 800,
          useNativeDriver: true,
        }),
      ]),
    );
    pulse.start();
    return () => pulse.stop();
  }, [opacity]);

  return (
    <View style={styles.container}>
      {Array.from({ length: rows }).map((_, i) => (
        <Animated.View
          key={i}
          style={[
            styles.row,
            {
              backgroundColor: theme.colors.surfaceVariant,
              opacity,
            },
          ]}
        >
          <View style={[styles.avatar, { backgroundColor: theme.colors.border }]} />
          <View style={styles.lines}>
            <View
              style={[
                styles.lineShort,
                { backgroundColor: theme.colors.border },
              ]}
            />
            <View
              style={[
                styles.lineLong,
                { backgroundColor: theme.colors.border },
              ]}
            />
          </View>
        </Animated.View>
      ))}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    gap: 12,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: 2,
    padding: 12,
    gap: 12,
  },
  avatar: {
    width: 36,
    height: 36,
    borderRadius: 2,
  },
  lines: {
    flex: 1,
    gap: 6,
  },
  lineShort: {
    height: 10,
    borderRadius: 4,
    width: '40%',
  },
  lineLong: {
    height: 10,
    borderRadius: 4,
    width: '70%',
  },
});
